import { Result, backendApi } from "./backendApi";

export enum AttributeWindowLinkTypeModel {
  Link = 1,
  Picture = 2,
  PDF = 3,
}

export interface AttributeWindowLinkModel {
  targetType: AttributeWindowLinkTypeModel;
  targetId: string;
}

export interface AttributeWindowModel {
  name?: string;
  title?: string;
  text?: string;
  pageNumber?: number;
  done?: boolean;
  links?: AttributeWindowLinkModel[];
  textErrors?: string[];
}

export interface LayerModel {
  name?: string;
  cssClassName?: string;
}

export interface StyleModel {
  name?: string;
  strokeWidth?: number;
  strokeColor?: string;
  strokeOpacity?: number;
  fillColor?: string;
  fillOpacity?: number;
}

class ImportDxfRequest {
  constructor(
    public vehicleFileNumber: string,
    public templateName: string | undefined,
    public templateTextTypeId: number,
    public reset: boolean,
    public tryToKeepOrder: boolean,
    public dxf: string,
  ) {}
}

class ImportDxfResponse {
  constructor(public warnings: string[]) {}
}

export enum LocaleModel {
  Dutch = 1,
  English,
  German,
  French,
  Spanish,
  Portuguese,
  Italian,
  Polish,
  Czech,
  Swedish,
  Danish,
  Norwegian,
  Finnish,
  Hungarian,
  Croatian,
  Hebrew,
  Slovene,
  Russian,
  Arabic,
  Ukrainian,
}

export const Locales = Object.values(LocaleModel)
  .map((item) => Number(item))
  .filter((item) => !isNaN(item));

class GetDrawingFileListRequest {
  constructor(
    public vehicleFileNumber: string,
    public type: DrawingFileTypeModel,
  ) {}
}

class GetDrawingFileListResponse {
  constructor(public files: DrawingFileModel[]) {}
}

export interface DrawingFileModel {
  name: string;
  locales: LocaleModel[];
}

class RemoveDrawingFileRequest {
  constructor(
    public vehicleFileNumber: string,
    public name: string,
    public locale: LocaleModel,
    public type: DrawingFileTypeModel,
  ) {}
}

export enum DrawingFileTypeModel {
  Picture = 1,
  PDF = 2,
}

class UploadDrawingFileResponse {
  constructor(public filename: string) {}
}

class GetDrawingTemplateListRequest {}

class GetDrawingTemplateListResponse {
  constructor(public templateNames: string[]) {}
}

class RemoveDrawingTemplateRequest {
  constructor(public name: string) {}
}

class ExportMpfzRequest {
  constructor(public vehicleFileNumber: string) {}
}

class GetMpfzPublishableRequest {
  constructor(public vehicleFileNumber: string) {}
}

class GetMpfzPublishableResponse {
  constructor(public mpfzIsPublishable: boolean) {}
}

class GetAttributeWindowRequest {
  constructor(
    public drawingName: string,
    public drawingType: DrawingTypeModel,
    public name: string,
    public isInformationWindow: boolean,
  ) {}
}

class GetAttributeWindowResponse {
  constructor(public attributeWindows: AttributeWindowModel[]) {}
}

class UpsertAttributeWindowRequest {
  constructor(
    public drawingName: string,
    public drawingType: DrawingTypeModel,
    public name: string | undefined,
    public isInformationWindow: boolean,
    public attributeWindows: AttributeWindowModel[],
  ) {}
}

class GetStyleRequest {
  constructor(
    public drawingName: string,
    public drawingType: DrawingTypeModel,
    public name: string,
  ) {}
}

class GetStyleResponse {
  constructor(public style: StyleModel) {}
}

class UpsertStyleRequest {
  constructor(
    public drawingName: string,
    public drawingType: DrawingTypeModel,
    public name: string | undefined,
    public style: StyleModel,
  ) {}
}

class RemoveStyleRequest {
  constructor(
    public drawingName: string,
    public drawingType: DrawingTypeModel,
    public name: string,
  ) {}
}

class GetLayerRequest {
  constructor(
    public drawingName: string,
    public drawingType: DrawingTypeModel,
    public name: string,
  ) {}
}

class GetLayerResponse {
  constructor(public layer: LayerModel) {}
}

class UpsertLayerRequest {
  constructor(
    public drawingName: string,
    public drawingType: DrawingTypeModel,
    public parentName: string,
    public name: string | undefined,
    public layer: LayerModel,
  ) {}
}

class RemoveLayerRequest {
  constructor(
    public drawingName: string,
    public drawingType: DrawingTypeModel,
    public name: string,
  ) {}
}

class MoveLayerRequest {
  constructor(
    public drawingName: string,
    public drawingType: DrawingTypeModel,
    public name: string,
    public direction: number,
  ) {}
}

export enum LayerNodeTypeModel {
  Layer = 1,
  Component = 2,
}

export class LayerNodeModel {
  constructor(
    public name: string,
    public type: LayerNodeTypeModel,
    public parentNodeName: string | undefined,
  ) {}
}

export class AttributeWindowNodeModel {
  constructor(
    public name: string,
    public done?: boolean,
  ) {}
}

export enum DrawingTypeModel {
  Template = 1,
  Drawing = 2,
}

class GetDrawingTreeRequest {
  constructor(
    public name: string,
    public type: DrawingTypeModel,
  ) {}
}

class GetDrawingTreeResponse {
  constructor(
    public styles: string[],
    public attributeWindows: AttributeWindowNodeModel[],
    public informationWindows: AttributeWindowNodeModel[],
    public layerNodes: LayerNodeModel[],
  ) {}
}

class GetAttributeWindowReferencesRequest {
  constructor(
    public drawingName: string,
    public drawingType: DrawingTypeModel,
  ) {}
}

class GetAttributeWindowReferencesResponse {
  constructor(public references: string[]) {}
}

class CreateDrawingTemplateRequest {
  constructor(
    public name: string,
    public basedOn: string | undefined,
  ) {}
}

export interface DrawingVersionModel {
  version: number;
  createdOn: Date;
  createdBy: string;
  releaseName?: string;
}

class GetDrawingVersionsResponse {
  constructor(public versions: DrawingVersionModel[]) {}
}

export const drawingApi = {
  async importMpfz(
    destinationVehicleFileNumber: string,
    sourceVehicleFileNumber: string,
    version: number | undefined,
  ): Promise<Result<any>> {
    return await backendApi.sendAsync("ImportMpfz", {
      sourceVehicleFileNumber: sourceVehicleFileNumber,
      destinationVehicleFileNumber: destinationVehicleFileNumber,
      version: version,
    });
  },

  async importVersion(
    vehicleFileNumber: string,
    version: number,
  ): Promise<Result<any>> {
    return await backendApi.sendAsync("ImportDrawingVersion", {
      vehicleFileNumber,
      version,
    });
  },
  async removeVersion(
    vehicleFileNumber: string,
    version: number,
  ): Promise<Result<any>> {
    return await backendApi.sendAsync("RemoveDrawingVersion", {
      vehicleFileNumber,
      version,
    });
  },
  async getVersions(
    vehicleFileNumber: string,
  ): Promise<Result<DrawingVersionModel[]>> {
    const response =
      await backendApi.sendAndGetResponseAsync<GetDrawingVersionsResponse>(
        "GetDrawingVersions",
        {
          vehicleFileNumber,
        },
      );

    if (response.ok) {
      response.value.versions.forEach((v) => {
        // convert the dates from string to actual date
        v.createdOn = new Date(v.createdOn);
      });
      return { ok: true, value: response.value.versions };
    }

    return response;
  },

  async renameDrawingFile(
    vehicleFileNumber: string,
    name: string,
    type: DrawingFileTypeModel,
    newName: string,
  ): Promise<Result<any>> {
    return await backendApi.sendAsync("RenameDrawingFile", {
      vehicleFileNumber,
      name,
      type,
      newName,
    });
  },

  async cleanupDrawingFiles(
    vehicleFileNumber: string,
    type: DrawingFileTypeModel,
  ): Promise<Result<any>> {
    return await backendApi.sendAsync("CleanupDrawingFiles", {
      vehicleFileNumber,
      type,
    });
  },

  async createDrawingTemplate(
    name: string,
    basedOn: string | undefined,
  ): Promise<Result<any>> {
    return await backendApi.sendAsync(
      "CreateDrawingTemplate",
      new CreateDrawingTemplateRequest(name, basedOn),
    );
  },

  async getAttributeWindowReferences(
    drawingName: string,
    drawingType: DrawingTypeModel,
  ): Promise<Result<string[]>> {
    var response =
      await backendApi.sendAndGetResponseAsync<GetAttributeWindowReferencesResponse>(
        "GetAttributeWindowReferences",
        new GetAttributeWindowReferencesRequest(drawingName, drawingType),
      );

    if (response.ok) {
      return { ok: true, value: response.value.references };
    }

    return response;
  },

  async getDrawingTree(
    name: string,
    type: DrawingTypeModel,
  ): Promise<Result<GetDrawingTreeResponse>> {
    const result =
      await backendApi.sendAndGetResponseAsync<GetDrawingTreeResponse>(
        "GetDrawingTree",
        new GetDrawingTreeRequest(name, type),
      );

    if (result.ok) {
      // enjoy null vs. undefined
      const attributeWindows = result.value.attributeWindows.map((aw) => ({
        ...aw,
        done: aw.done === null ? undefined : aw.done,
      }));

      const informationWindows = result.value.informationWindows.map((aw) => ({
        ...aw,
        done: aw.done === null ? undefined : aw.done,
      }));

      return {
        ok: true,
        value: { ...result.value, ...{ attributeWindows, informationWindows } },
      };
    }

    return result;
  },
  async moveLayer(
    drawingName: string,
    drawingType: DrawingTypeModel,
    name: string,
    direction: number,
  ): Promise<Result<any>> {
    return await backendApi.sendAsync(
      "MoveLayer",
      new MoveLayerRequest(drawingName, drawingType, name, direction),
    );
  },

  async removeLayer(
    drawingName: string,
    drawingType: DrawingTypeModel,
    name: string,
  ): Promise<Result<any>> {
    return await backendApi.sendAsync(
      "RemoveLayer",
      new RemoveLayerRequest(drawingName, drawingType, name),
    );
  },
  async upsertLayer(
    drawingName: string,
    drawingType: DrawingTypeModel,
    parentName: string,
    name: string | undefined,
    layer: LayerModel,
  ): Promise<Result<any>> {
    return await backendApi.sendAsync(
      "UpsertLayer",
      new UpsertLayerRequest(drawingName, drawingType, parentName, name, layer),
    );
  },

  async getLayer(
    drawingName: string,
    drawingType: DrawingTypeModel,
    name: string,
  ): Promise<Result<LayerModel>> {
    const response = await backendApi.sendAndGetResponseAsync<GetLayerResponse>(
      "GetLayer",
      new GetLayerRequest(drawingName, drawingType, name),
    );

    if (response.ok) {
      return { ok: true, value: response.value.layer };
    }

    return response;
  },

  async removeStyle(
    drawingName: string,
    drawingType: DrawingTypeModel,
    name: string,
  ): Promise<Result<any>> {
    return await backendApi.sendAsync(
      "RemoveStyle",
      new RemoveStyleRequest(drawingName, drawingType, name),
    );
  },
  async upsertStyle(
    drawingName: string,
    drawingType: DrawingTypeModel,
    name: string | undefined,
    style: StyleModel,
  ): Promise<Result<any>> {
    return await backendApi.sendAsync(
      "UpsertStyle",
      new UpsertStyleRequest(drawingName, drawingType, name, style),
    );
  },

  async getStyle(
    drawingName: string,
    drawingType: DrawingTypeModel,
    name: string,
  ): Promise<Result<StyleModel>> {
    const response = await backendApi.sendAndGetResponseAsync<GetStyleResponse>(
      "GetStyle",
      new GetStyleRequest(drawingName, drawingType, name),
    );

    if (response.ok) {
      return { ok: true, value: response.value.style };
    }

    return response;
  },

  async upsertAttributeWindow(
    drawingName: string,
    drawingType: DrawingTypeModel,
    name: string | undefined,
    isInformationWindow: boolean,
    attributeWindows: AttributeWindowModel[],
  ): Promise<Result<any>> {
    return await backendApi.sendAsync(
      "UpsertAttributeWindow",
      new UpsertAttributeWindowRequest(
        drawingName,
        drawingType,
        name,
        isInformationWindow,
        attributeWindows,
      ),
    );
  },

  async getAttributeWindow(
    drawingName: string,
    drawingType: DrawingTypeModel,
    name: string,
    isInformationWindow: boolean,
  ): Promise<Result<AttributeWindowModel[]>> {
    const response =
      await backendApi.sendAndGetResponseAsync<GetAttributeWindowResponse>(
        "GetAttributeWindow",
        new GetAttributeWindowRequest(
          drawingName,
          drawingType,
          name,
          isInformationWindow,
        ),
      );

    if (response.ok) {
      return {
        ok: true,
        value: response.value.attributeWindows.map((aw) => ({
          ...aw,
          done: aw.done === null ? undefined : aw.done,
        })),
      };
    }

    return response;
  },

  async exportMpfz(vehicleFileNumber: string): Promise<Result<any>> {
    return await backendApi.sendAsync(
      "ExportMpfz",
      new ExportMpfzRequest(vehicleFileNumber),
    );
  },

  async getMpfzPublishable(
    vehicleFileNumber: string,
  ): Promise<Result<GetMpfzPublishableResponse>> {
    var response =
      await backendApi.sendAndGetResponseAsync<GetMpfzPublishableResponse>(
        "GetMpfzPublishable",
        new GetMpfzPublishableRequest(vehicleFileNumber),
      );

    if (response.ok) {
      return { ok: true, value: response.value };
    }

    return response;
  },

  async removeDrawingFile(
    vehicleFileNumber: string,
    name: string,
    type: DrawingFileTypeModel,
    locale: LocaleModel,
  ) {
    const result = await backendApi.sendAsync(
      "RemoveDrawingFile",
      new RemoveDrawingFileRequest(vehicleFileNumber, name, locale, type),
    );

    return result;
  },

  async uploadDrawingFile(
    vehicleFileNumber: string,
    file: File,
    filename: string,
    locale: LocaleModel,
    type: DrawingFileTypeModel,
  ): Promise<Result<string>> {
    const result =
      await backendApi.uploadFileWithResponseAsync<UploadDrawingFileResponse>(
        `drawingFile?vehicleFileNumber=${vehicleFileNumber}&type=${type}&locale=${locale}`,
        file,
        filename,
      );

    if (result.ok) {
      return { ok: true, value: result.value.filename };
    }

    return result;
  },

  async getDrawingFiles(
    vehicleFileNumber: string,
    type: DrawingFileTypeModel,
  ): Promise<Result<DrawingFileModel[]>> {
    const result =
      await backendApi.sendAndGetResponseAsync<GetDrawingFileListResponse>(
        "GetDrawingFileList",
        new GetDrawingFileListRequest(vehicleFileNumber, type),
      );

    if (result.ok) {
      return { ok: true, value: result.value.files };
    }

    return result;
  },

  async downloadDrawingFile(
    vehicleFileNumber: string,
    pdfId: string,
    type: DrawingFileTypeModel,
    locale: LocaleModel,
    showInline: boolean,
  ): Promise<Result<any>> {
    let response = await backendApi.downloadFileAsync(
      `drawingFile?vehicleFileNumber=${vehicleFileNumber}&fileId=${encodeURIComponent(
        pdfId,
      )}&type=${type}&locale=${locale}&inline=${showInline}`,
    );

    return response;
  },

  async removeDrawingTemplate(name: string): Promise<Result<any>> {
    return await backendApi.sendAsync(
      "RemoveDrawingTemplate",
      new RemoveDrawingTemplateRequest(name),
    );
  },

  async getDrawingTemplateList(): Promise<Result<string[]>> {
    const result =
      await backendApi.sendAndGetResponseAsync<GetDrawingTemplateListResponse>(
        "GetDrawingTemplateList",
        new GetDrawingTemplateListRequest(),
      );

    if (result.ok) {
      return { ok: true, value: result.value.templateNames };
    }

    return result;
  },

  async importDxf(
    vehicleFileNumber: string,
    templateName: string | undefined,
    templateTextTypeId: number,
    reset: boolean,
    tryToKeepOrder: boolean,
    dxf: string,
  ): Promise<Result<[warnings: string[]]>> {
    const result = await backendApi.sendAndGetResponseAsync<ImportDxfResponse>(
      "ImportDxf",
      new ImportDxfRequest(
        vehicleFileNumber,
        templateName,
        templateTextTypeId,
        reset,
        tryToKeepOrder,
        dxf,
      ),
    );

    if (result.ok) {
      return { ok: true, value: [result.value.warnings] };
    }

    return result;
  },
};
